(() => {
  const SECTION_SELECTOR = '.product-pdp';

  /* ------------------------------------------------------------------ */
  /* Lightbox (shared, lazy-created)                                     */
  /* ------------------------------------------------------------------ */

  let lightbox = null;

  function createLightbox() {
    const root = document.createElement('div');
    root.className = 'product-pdp-lightbox';
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.innerHTML = `
      <div class="product-pdp-lightbox__stage">
        <button type="button" class="product-pdp-lightbox__close" aria-label="Close">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <button type="button" class="product-pdp-lightbox__nav product-pdp-lightbox__nav--prev" aria-label="Previous">
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M22 0.75C33.7361 0.75 43.25 10.2639 43.25 22C43.25 33.7361 33.7361 43.25 22 43.25C10.2639 43.25 0.75 33.7361 0.75 22C0.75 10.2639 10.2639 0.75 22 0.75Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M19.5 27.5L13.5 22L19.5 16.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M13.5 22L29.5 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
        <div class="product-pdp-lightbox__image-wrap">
          <img class="product-pdp-lightbox__image" alt="" decoding="async" fetchpriority="high" />
          <span class="product-pdp-lightbox__spinner" aria-hidden="true"></span>
        </div>
        <p class="product-pdp-lightbox__caption"></p>
        <button type="button" class="product-pdp-lightbox__nav product-pdp-lightbox__nav--next" aria-label="Next">
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M22 0.75C33.7361 0.75 43.25 10.2639 43.25 22C43.25 33.7361 33.7361 43.25 22 43.25C10.2639 43.25 0.75 33.7361 0.75 22C0.75 10.2639 10.2639 0.75 22 0.75Z" stroke="currentColor" stroke-width="1.5"/>
            <path d="M23.5 16.5L29.5 22L23.5 27.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M29.5 22L13.5 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;
    document.body.appendChild(root);

    const state = {
      root,
      img: root.querySelector('.product-pdp-lightbox__image'),
      caption: root.querySelector('.product-pdp-lightbox__caption'),
      prev: root.querySelector('.product-pdp-lightbox__nav--prev'),
      next: root.querySelector('.product-pdp-lightbox__nav--next'),
      close: root.querySelector('.product-pdp-lightbox__close'),
      items: [],
      index: 0,
      lastFocus: null,
      renderToken: 0,
    };

    const preload = (i) => {
      [i - 1, i + 1].forEach((adj) => {
        const item = state.items[adj];
        if (!item || !item.url) return;
        const img = new Image();
        img.src = item.url;
      });
    };

    const render = async () => {
      const item = state.items[state.index];
      if (!item) return;
      const token = ++state.renderToken;

      state.caption.textContent = item.title || '';
      state.caption.hidden = !item.title;
      state.prev.disabled = state.index <= 0;
      state.next.disabled = state.index >= state.items.length - 1;

      state.root.classList.add('is-loading');
      state.img.alt = item.title || '';
      state.img.src = item.url || '';

      try {
        await state.img.decode();
      } catch (_) {
        /* image was changed/aborted */
      }

      if (token !== state.renderToken) return;
      state.root.classList.remove('is-loading');
      preload(state.index);
    };

    const goTo = (i) => {
      if (i < 0 || i >= state.items.length) return;
      state.index = i;
      render();
    };

    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') goTo(state.index - 1);
      else if (e.key === 'ArrowRight') goTo(state.index + 1);
    };

    const close = () => {
      state.root.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
        state.lastFocus.focus();
      }
    };

    state.prev.addEventListener('click', () => goTo(state.index - 1));
    state.next.addEventListener('click', () => goTo(state.index + 1));
    state.close.addEventListener('click', close);
    state.root.addEventListener('click', (e) => {
      if (e.target === state.root) close();
    });

    state.open = (items, index) => {
      state.items = items || [];
      state.index = Math.max(0, Math.min(index || 0, state.items.length - 1));
      state.lastFocus = document.activeElement;
      state.root.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
      render();
      state.close.focus();
    };

    return state;
  }

  function getLightbox() {
    if (!lightbox) lightbox = createLightbox();
    return lightbox;
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                              */
  /* ------------------------------------------------------------------ */

  function formatMoney(cents, format) {
    if (!format) return cents;
    const amount = (cents / 100).toFixed(2);
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace(
        '{{amount_no_decimals_with_comma_separator}}',
        String(Math.round(cents / 100)).replace('.', ',')
      );
  }

  /* ------------------------------------------------------------------ */
  /* Gallery — thumbnail switching + swipe                               */
  /* ------------------------------------------------------------------ */

  function initGallery(section) {
    const mainMedia = section.querySelector('[data-main-media]');
    const thumbs = section.querySelectorAll('[data-thumb-index]');
    if (!mainMedia) return;

    function activateIndex(index) {
      mainMedia.querySelectorAll('[data-media-index]').forEach((item) => {
        const active = Number(item.dataset.mediaIndex) === index;
        item.classList.toggle('product-pdp__media-item--active', active);
      });
      thumbs.forEach((btn) => {
        const active = Number(btn.dataset.thumbIndex) === index;
        btn.classList.toggle('product-pdp__thumb--active', active);
        btn.setAttribute('aria-pressed', String(active));
        if (active && typeof btn.scrollIntoView === 'function') {
          btn.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
        }
      });
    }

    function activateByMediaId(mediaId) {
      const all = [...mainMedia.querySelectorAll('[data-media-id]')];
      const idx = all.findIndex((el) => el.dataset.mediaId === String(mediaId));
      if (idx !== -1) activateIndex(idx);
    }

    thumbs.forEach((btn) => {
      btn.addEventListener('click', () => activateIndex(Number(btn.dataset.thumbIndex)));
    });

    /* Swipe on mobile */
    let startX = 0;
    let dragging = false;

    mainMedia.addEventListener('pointerdown', (e) => {
      startX = e.clientX;
      dragging = true;
    }, { passive: true });

    mainMedia.addEventListener('pointerup', (e) => {
      if (!dragging) return;
      dragging = false;
      const dx = e.clientX - startX;
      if (Math.abs(dx) < 40) return;

      const all = mainMedia.querySelectorAll('[data-media-index]');
      const current = [...all].findIndex((el) =>
        el.classList.contains('product-pdp__media-item--active')
      );
      const next = dx < 0
        ? Math.min(current + 1, all.length - 1)
        : Math.max(current - 1, 0);

      if (next !== current) activateIndex(next);
    }, { passive: true });

    /* Exposed so variant init can switch images */
    return { activateByMediaId };
  }

  /* ------------------------------------------------------------------ */
  /* Variants + quantity                                                  */
  /* ------------------------------------------------------------------ */

  function initVariants(section, gallery) {
    const form = section.querySelector('[data-product-form]');
    if (!form) return;

    const variantInput = form.querySelector('[data-variant-input]');
    if (!variantInput) return;

    const variantsJSON = section.querySelector('[data-variants]');
    if (!variantsJSON) return;

    const variants = JSON.parse(variantsJSON.textContent || '[]');
    if (!variants.length) return;

    const moneyFormatEl = section.querySelector('[data-money-format]');
    const moneyFormat = moneyFormatEl ? JSON.parse(moneyFormatEl.textContent) : null;

    const priceEl = section.querySelector('[id^="product-pdp-price-"]');
    const skuEl = section.querySelector('[id^="product-pdp-sku-"]');
    const addBtn = form.querySelector('[data-add-btn]');

    /* Build selections map from initially active pills */
    const selections = {};
    form.querySelectorAll('.product-pdp__option-btn--active').forEach((btn) => {
      selections[Number(btn.dataset.optionPosition)] = btn.dataset.optionValue;
    });

    function findVariant() {
      return variants.find((v) =>
        Object.entries(selections).every(
          ([idx, val]) => v[`option${Number(idx) + 1}`] === val
        )
      );
    }

    function updateUI(variant) {
      if (!variant) return;

      variantInput.value = variant.id;

      /* Price */
      if (priceEl) {
        const currentEl = priceEl.querySelector('.product-pdp__price-current');
        const compareEl = priceEl.querySelector('.product-pdp__price-compare');
        if (currentEl) currentEl.textContent = formatMoney(variant.price, moneyFormat);
        if (compareEl) {
          if (variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price, moneyFormat);
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.setAttribute('hidden', '');
          }
        }
      }

      /* SKU */
      if (skuEl) {
        if (variant.sku) {
          skuEl.textContent = (skuEl.dataset.label || 'SKU ') + variant.sku;
          skuEl.removeAttribute('hidden');
        } else {
          skuEl.setAttribute('hidden', '');
        }
      }

      /* Add button */
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available
          ? (addBtn.dataset.addLabel || '')
          : (addBtn.dataset.soldOutLabel || '');
      }

      /* Gallery — switch to variant's featured image */
      if (gallery && variant.featured_media) {
        gallery.activateByMediaId(variant.featured_media.id);
      }

      /* Update URL param */
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /* Pill clicks */
    form.querySelectorAll('[data-option-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.getAttribute('aria-disabled') === 'true') return;

        const position = Number(btn.dataset.optionPosition);
        form.querySelectorAll(`[data-option-position="${position}"]`).forEach((b) => {
          b.classList.toggle('product-pdp__option-btn--active', b === btn);
        });

        selections[position] = btn.dataset.optionValue;
        const variant = findVariant();
        if (variant) updateUI(variant);
      });
    });

    /* Quantity stepper */
    const qtyInput = form.querySelector('[data-qty-input]');
    const qtyMinus = form.querySelector('[data-qty-minus]');
    const qtyPlus = form.querySelector('[data-qty-plus]');

    if (qtyInput && qtyMinus && qtyPlus) {
      qtyMinus.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10) || 1;
        if (val > 1) qtyInput.value = val - 1;
      });
      qtyPlus.addEventListener('click', () => {
        const val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
      });
      qtyInput.addEventListener('change', () => {
        const val = parseInt(qtyInput.value, 10);
        if (isNaN(val) || val < 1) qtyInput.value = 1;
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Flavour picker — accordion toggle (rows are navigation links)       */
  /* ------------------------------------------------------------------ */

  function initFlavourPickers(section) {
    section.querySelectorAll('[data-flavour-picker]').forEach((picker) => {
      const trigger = picker.querySelector('[data-flavour-trigger]');
      const panel   = picker.querySelector('[data-flavour-panel]');
      if (!trigger || !panel) return;

      function updateFade() {
        const atBottom = panel.scrollTop + panel.offsetHeight >= panel.scrollHeight - 4;
        panel.classList.toggle('is-at-bottom', atBottom);
      }

      panel.addEventListener('scroll', updateFade, { passive: true });

      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!isOpen));
        if (isOpen) {
          panel.setAttribute('hidden', '');
        } else {
          panel.removeAttribute('hidden');
          requestAnimationFrame(updateFade);
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */

  function initLightboxTriggers(section) {
    const mainMedia = section.querySelector('[data-main-media]');
    if (!mainMedia) return;

    let items = [];
    try {
      items = JSON.parse(mainMedia.dataset.pdpLightboxItems || '[]');
    } catch (_) {
      items = [];
    }
    if (!items.length) return;

    const currentIndex = () => {
      const active = mainMedia.querySelector('.product-pdp__media-item--active');
      return active ? Number(active.dataset.mediaIndex) || 0 : 0;
    };

    const openLightbox = (e) => {
      e.preventDefault();
      getLightbox().open(items, currentIndex());
    };

    const zoomBtn = mainMedia.querySelector('[data-pdp-zoom]');
    if (zoomBtn) zoomBtn.addEventListener('click', openLightbox);

    mainMedia.querySelectorAll('.product-pdp__main-image').forEach((img) => {
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', openLightbox);
    });
  }

  function initSection(section) {
    if (section.dataset.productInit) return;
    section.dataset.productInit = 'true';
    const gallery = initGallery(section);
    initVariants(section, gallery);
    initFlavourPickers(section);
    initLightboxTriggers(section);
  }

  function initAll(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });
})();

/* Product enquiry modal
-------------------------------------------------- */

document.addEventListener('DOMContentLoaded', () => {
  const enquiryTriggers = document.querySelectorAll(
    '[data-product-enquiry-open]'
  );

  enquiryTriggers.forEach((trigger) => {
    const modalId = trigger.getAttribute('aria-controls');
    const modal = document.getElementById(modalId);

    if (!modal) return;

    const closeButtons = modal.querySelectorAll(
      '[data-product-enquiry-close]'
    );

    const closeModal = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-enquiry-open');

      trigger.focus();
    };

    const openModal = () => {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-enquiry-open');

      const closeButton = modal.querySelector(
        '.product-pdp__enquiry-close'
      );

      if (closeButton) {
        closeButton.focus();
      }
    };

    trigger.addEventListener('click', openModal);

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    });
  });
});
